import 'firebase/auth'
import 'firebase/firestore'

import axios from 'axios'
import { AuthenticationRepositoryType, AuthUser, GeneralError } from '51trips-frontend-core'
import {
  ConfirmationResult,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  onAuthStateChanged,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut
} from 'firebase/auth'
import { Observable } from 'rxjs'

import { auth } from '../firebase'
import authUserConverter from './converters/AuthUserConverter'
import generalErrorConverter from './converters/GeneralErrorConverter'
import {setUser} from "../features/auth.ts";
import {useDispatch} from "react-redux";

const EMAIL_FOR_SIGN_IN_STORAGE_KEY = 'emailForSignIn'

export class AuthenticationRepository implements AuthenticationRepositoryType {
  get authenticatedUserID() {
    return auth.currentUser?.uid
  }

  get authenticatedUserProviderID() {
    return auth.currentUser?.providerData[0].providerId
  }

  login(email: string, password: string) {
    return new Observable<string>((subscriber) => {
      signInWithEmailAndPassword(auth, email, password)
        .then((userCredential) => {
          const userIdentifier = userCredential.user.uid
          subscriber.next(userIdentifier)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(error, `login for email ${email} failed`)
          )
        })
    })
  }

  googleSignIn(userEmail?: string) {
    return new Observable<AuthUser>((subscriber) => {
      const provider = new GoogleAuthProvider()
      if (userEmail) {
        provider.setCustomParameters({
          'login_hint': userEmail
        })
      }

      signInWithPopup(auth, provider)
        .then((result) => {
          const additionalUserInfo = getAdditionalUserInfo(result)
          const authUser = authUserConverter.fromGoogle(result.user, additionalUserInfo)
          subscriber.next(authUser)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromGoogleSignInError(error, 'Google sign in failed')
          )
        })
    })
  }

  appleSignIn() {
    return new Observable<AuthUser>((subscriber) => {
      const provider = new OAuthProvider('apple.com')
      signInWithPopup(auth, provider)
        .then((result) => {
          const additionalUserInfo = getAdditionalUserInfo(result)
          const authUser = authUserConverter.fromApple(result.user, additionalUserInfo)
          subscriber.next(authUser)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromAppleSignInError(error, 'Apple sign in failed')
          )
        })
    })
  }

  signup(email: string, password: string) {
    return new Observable<string>((subscriber) => {
      createUserWithEmailAndPassword(auth, email, password)
        .then((userCredential) => {
          const userIdentifier = userCredential.user.uid
          subscriber.next(userIdentifier)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(error, `login for email ${email} failed`)
          )
        })
    })
  }

  resetPassword(email: string) {
    return new Observable<void>((subscriber) => {
      sendPasswordResetEmail(auth, email)
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(
              error,
              `resetPassword for email ${email} failed`
            )
          )
        })
    })
  }

  sendPhoneVerificationCode(phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) {
    return new Observable<ConfirmationResult>((subscriber) => {
      signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
        .then((confirmationResult) => {
          subscriber.next(confirmationResult)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(
              error,
              `sendPhoneVerificationCode for phone ${phoneNumber} failed`
            )
          )
        })
    })
  }

  confirmPhoneVerificationCode(confirmationResult: ConfirmationResult, code: string) {
    return new Observable<string>((subscriber) => {
      confirmationResult
        .confirm(code)
        .then((userCredential) => {
          subscriber.next(userCredential.user.uid)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(error, 'confirmPhoneVerificationCode failed')
          )
        })
    })
  }

  // Routed through our own backend (POST /internal/auth/magic-link) instead
  // of calling Firebase's identitytoolkit API directly: the backend emails
  // the link itself via Brevo (our transactional-email templates), rather
  // than Firebase's own default email. This is a public, unauthenticated
  // endpoint — no Bearer token to attach, since there's no session yet — and
  // it always resolves (even for an email with no account), so the UI can't
  // be used to probe which emails are registered.
  sendMagicLink(email: string) {
    return new Observable<void>((subscriber) => {
      const continueUrl = `${window.location.origin}/auth/login`

      axios.post(`${import.meta.env.VITE_APP_SERVER_URL}/internal/auth/magic-link`, {email, continueUrl})
        .then(() => {
          window.localStorage.setItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY, email)
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromBackendServerError(error)
          )
        })
    })
  }

  isMagicLinkSignIn(url: string) {
    return isSignInWithEmailLink(auth, url)
  }

  get storedEmailForMagicLinkSignIn() {
    return window.localStorage.getItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY) ?? undefined
  }

  signInWithMagicLink(email: string, url: string) {
    return new Observable<string>((subscriber) => {
      signInWithEmailLink(auth, email, url)
        .then((userCredential) => {
          window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY)
          subscriber.next(userCredential.user.uid)
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseAuthError(error, 'signInWithMagicLink failed')
          )
        })
    })
  }

  logout() {
    return new Observable<void>((subscriber) => {
      signOut(auth)
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(generalErrorConverter.fromFirebaseAuthError(error, 'logout failed'))
        })
    })
  }

  isSignedInWithGoogle() {
    return new Observable<boolean>((subscriber) => {
      const user = auth.currentUser
      if (user) {
        const isGoogleUser = user.providerData.some(
          (provider) => provider.providerId === GoogleAuthProvider.PROVIDER_ID
        )
        subscriber.next(isGoogleUser)
        subscriber.complete()
      } else {
        subscriber.next(false)
        subscriber.complete()
      }
    })
  }

  googleSignOut() {
    return this.logout()
  }

  isAuthenticated() {
    return new Observable<boolean>((subscriber) => {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          // @ts-ignore
          if (window.posthog) {
            // @ts-ignore
            window.posthog.identify(user.uid)
          }
          subscriber.next(true)
        } else {
          subscriber.next(false)
        }
        subscriber.complete()
      })
    })
  }

  deleteUser() {
    return new Observable<void>((subscriber) => {
      const authenticatedUser = auth.currentUser
      if (authenticatedUser) {
        authenticatedUser
          .delete()
          .then(() => {
            subscriber.next()
            subscriber.complete()
          })
          .catch((error) => {
            subscriber.error(
              generalErrorConverter.fromFirebaseAuthError(error, 'delete for user failed')
            )
          })
      } else {
        subscriber.error(new GeneralError('There is no user authenticated'))
      }
    })
  }

  getIDToken() {
    return new Observable<string>((subscriber) => {
      const authenticatedUser = auth.currentUser
      if (authenticatedUser) {
        authenticatedUser
          .getIdToken()
          .then((token) => {
            subscriber.next(token)
            subscriber.complete()
          })
          .catch((error) => {
            subscriber.error(
              generalErrorConverter.fromFirebaseAuthError(error, 'get id token for user failed')
            )
          })
      } else {
        subscriber.error(new GeneralError('There is no user authenticated'))
      }
    })
  }
}
